import React, { useContext } from "react";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import PageContext from "../../components/Common/PageContext";
import { PageContextType } from "../../components/Common/PageContextType";
import {
	addOrUpdateReceiveToReceivesList,
	isPurchaseOrderLocked,
	mapReceivesWithAdditionalDetails,
} from "../../util/receiveUtil";
import { MemorizedManualReceiveEdit } from "../../components/Receive/ManualReceiveEdit";
import TabGroup from "../../components/Tab/TabGroup";
import { receiveStatus } from "../../util/receiveStatus";
import { speedDialAction } from "../../util/speedDialUtil";
import SpeedDialMenu from "../../components/SpeedDial/SpeedDialMenu";
import ModalAssignLocation from "../../components/Modal/ModalAssignLocation";
import { ManualHeaderDetails } from "../../components/Invoice/ManualHeaderDetails";
import CommercialSelect from "../../components/Select/CommercialSelect";
import { useInitialization } from "./hooks/useInitialization";
import { useReceiveOperations } from "./hooks/useReceiveOperations";
import { useModal } from "./hooks/useModal";
import { Backdrop, useTheme } from "@mui/material";
import PreviousReceiveList from "../../components/Receive/PreviousReceiveList";
import { useScan } from "./hooks/useScan";
import { receiveType } from "../../util/receiveTypeMapping";
import { Modal } from "../../components/Modal/Modal";
import AlertModal from "../../components/AlertModal/AlertModal";
import ModalReceiveFinalise from "../../components/Modal/ModalReceiveFinalise";
import { ModalHistory } from "../../components/Modal/ModalHistory";
import alertTypeEnum from "../../util/alertTypeEnum";
import { ReceiveHistory } from "../../components/History/ReceiveHistory";
import modalSize from "../../util/modalSizeEnum";
import { MemorizedReadonlyManualReceiveLineItemTable } from "../../components/Invoice/ManualReceiveLineItemTable";
import { useComments } from "./hooks/useComments";
import { CommentModal } from "../../components/Modal/CommentModal";

const ManualReceiving: React.FC = () => {
	const theme = useTheme();
	const {
		pageData,
		loadingDialog,
		setLoadingDialog,
		receive,
		setReceive,
		receives,
		setReceives,
		originalReceives,
		locationBays,
		isInReview,
		purchaseOrderLineItems,
		activeTab,
		setActiveTab,
		canPurchaseOrderBeReopened,
		enrichedReceive,
		productDetails,
		isPartReceiveFeatureEnabled,
		enableCommercialAccountSelection,
		enabledArrivals,
		enabledPriorityStock,
		captureFreightCostEnabled,
		enableAssignIbLocation,
		enableProductLabelAutoPrint,
		hasLabels,
		modal,
		returnToHome,
		setModal,
		alertHeader,
		setAlertHeader,
		editableInvoiceFields,
		handleInvoiceFieldChanged,
		handlePreviousReceiveFieldChanged,
		ordersAlreadyAllocated,
		welcomeModalShownThisSession,
		initialised,
		setIsActionPrevented,
		currentScannedProduct,
		setCurrentScannedProduct,
		selectedCommercialAccount,
		setSelectedCommercialAccount,
		inboundLocation,
		setInboundLocation,
	} = useInitialization(receiveType.manual);

	const {
		isExpanded,
		isHardwareView,
		onReceivedQuantityChange,
		onCostChange,
		onPreviousReceiveQuantityChanged,
		onPreviousReceiveCostChange,
		upsertReceive,
		onExpandHeaderChange,
		onInvoiceExpandChange,
		toggleView,
		onCloseModal,
		onSetInProgressReceive,
		clearCurrentReceive,
		setIsExpanded,
	} = useReceiveOperations(
		activeTab,
		receive,
		setReceive,
		receives,
		setReceives,
		pageData?.purchaseOrder,
		setModal,
		setLoadingDialog,
		pageData?.priorityStock ?? [],
		enabledPriorityStock,
		enabledArrivals,
		setAlertHeader,
		setCurrentScannedProduct
	);

	const {
		isModalReceiveFinaliseOpen,
		isBackdropOpen,
		isSpeedDialOpen,
		alertModal,
		assignLocation,
		setAssignLocation,
		onOpenSpeedDial,
		onCloseSpeedDial,
		checkInvoiceNumberIsValidAsync,
		onSaveDraftClick,
		onSaveReceivesInReviewClick,
		onSubmitForReviewClick,
		onSubmitForReview,
		onFinaliseReceivesInReviewClick,
		onReopenPoClick,
		onReceiveEditClick,
		onClickClearReceive,
		onLocationNameChange,
		onLocationSubmit,
		onCloseModalReceiveFinalise,
		onCloseAlertModal,
		closingHistoryModal,
		onReceiveHistoryClick,
		receiveHistory,
		onStartReviewClick,
		onCancelReviewClick,
	} = useModal(
		pageData,
		activeTab,
		receive,
		receives,
		productDetails,
		originalReceives,
		setModal,
		setLoadingDialog,
		setReceive,
		setReceives,
		setActiveTab,
		upsertReceive,
		onCloseModal,
		returnToHome,
		onSetInProgressReceive,
		clearCurrentReceive,
		setIsExpanded,
		welcomeModalShownThisSession,
		isPartReceiveFeatureEnabled,
		receiveType.manual,
		initialised,
		setIsActionPrevented,
		editableInvoiceFields,
		selectedCommercialAccount
	);

	const { onSaveItemClick, onDataChange } = useScan({
		purchaseOrder: pageData?.purchaseOrder,
		receiveType: receiveType.manual,
		receive,
		productDetails,
		invoiceFields: editableInvoiceFields,
		receives,
		setReceives,
		setReceive,
		onReceivedQuantityChange,
		setCurrentScannedProduct,
		inboundLocation,
		locationBays,
		activeTab,
		assignLocation,
		setModal,
		setIsExpanded,
		setInboundLocation,
		setAssignLocation,
		setActiveTab,
		onCloseModal,
		setLoadingDialog,
		enableAssignIbLocation,
		selectedCommercialAccount,
	});

	const [pageContext] = useContext(PageContext) as PageContextType;

	const { comments, commentModal, onCommentModalOpen, onCommentModalClose, onAddComment } = useComments(
		receive?.comments || []
	);

	const { commercialAccounts, userLocation } = pageContext;

	return (
		<>
			{pageData && (
				<>
					<ManualHeaderDetails
						isExpanded={isExpanded.header}
						isLocked={isPurchaseOrderLocked(pageData.receives)}
						onExpandClick={onExpandHeaderChange}
						purchaseOrder={pageData.purchaseOrder}
						showLock={true}
						alert={alertHeader}
					/>
					{enableCommercialAccountSelection &&
						commercialAccounts?.length > 0 &&
						userLocation === parseInt(pageData.purchaseOrder.shipToLocation?.locationId) && (
							<CommercialSelect
								id="Commerical Account"
								menuItems={commercialAccounts}
								onChange={setSelectedCommercialAccount}
								currentValue={selectedCommercialAccount}
							/>
						)}
					<TabGroup
						handleTabChange={setActiveTab}
						activeTab={activeTab}
						tabPanels={[
							{
								label: "Details",
								child: (
									<MemorizedReadonlyManualReceiveLineItemTable
										invoiceNumber={receive?.invoiceNumber ?? ""}
										lineItems={purchaseOrderLineItems}
										isHardwareView={isHardwareView}
										isLocationBayEditable={false}
										showDetailsOnly={true}
										showOverReceive={false}
										onLocationNameChange={() => {}}
										priorityStock={pageData.priorityStock || []}
										isPriorityStockEnabled={enabledPriorityStock}
										isArrivalsFeatureEnabled={enabledArrivals}
									/>
								),
							},
							{
								label: "Receive",
								child: (
									<>
										<MemorizedManualReceiveEdit
											validateInvoiceNumber={checkInvoiceNumberIsValidAsync}
											receive={enrichedReceive}
											storeName={pageData.purchaseOrder.shipToLocation.companyName}
											editableInvoiceFields={editableInvoiceFields}
											handleInvoiceFieldChanged={handleInvoiceFieldChanged}
											currentScannedProduct={currentScannedProduct}
											onExpandInvoiceClick={onInvoiceExpandChange}
											isInvoiceExpanded={isExpanded.invoiceDetails}
											onReceivedQuantityChange={(sku, qty) => {
												if (!receive) return;

												const updatedReceive = onReceivedQuantityChange({ receive, sku, newQuantity: qty });
												setReceive(updatedReceive);
												setReceives(addOrUpdateReceiveToReceivesList(receives, updatedReceive));
											}}
											onCostChange={(sku, unitInc, unitEx, lineInc, lineEx) => {
												if (!receive) return;

												const updatedReceive = onCostChange({
													receive,
													sku,
													unitIncCost: unitInc,
													unitExCost: unitEx,
													lineIncCost: lineInc,
													lineExCost: lineEx,
												});
												setReceive(updatedReceive);
												setReceives(addOrUpdateReceiveToReceivesList(receives, updatedReceive));
											}}
											onDataChange={onDataChange}
											onSaveItemClick={onSaveItemClick}
											isHardwareView={isHardwareView}
											onLocationNameChange={onLocationNameChange}
											currentLocation={inboundLocation}
											locationBays={locationBays}
											enableAssignIbLocation={enableAssignIbLocation}
											priorityStock={pageData.priorityStock || []}
											isPriorityStockEnabled={enabledPriorityStock}
											isArrivalsFeatureEnabled={enabledArrivals}
											captureFreightCostEnabled={captureFreightCostEnabled}
										/>
									</>
								),
							},
							{
								label: "Review",
								child: (
									<PreviousReceiveList
										receives={mapReceivesWithAdditionalDetails(receives, pageData.purchaseOrder, productDetails)}
										onReceiveEditClick={onReceiveEditClick}
										onReceivedQuantityChange={(receiveId, sku, qty) => {
											onPreviousReceiveQuantityChanged({ receiveId, sku, newQuantity: qty });
										}}
										onCostChange={(receiveId, sku, unitInc, unitEx, lineInc, lineEx) =>
											onPreviousReceiveCostChange({
												receiveId,
												sku,
												unitIncCost: unitInc,
												unitExCost: unitEx,
												lineIncCost: lineInc,
												lineExCost: lineEx,
											})
										}
										showDetailsOnly={false}
										isHardwareView={isHardwareView}
										onSetReceiveProperty={handlePreviousReceiveFieldChanged}
										onStartReview={onStartReviewClick}
										onCancelReview={onCancelReviewClick}
										isLockedForReview={isInReview}
										onHistoryClick={onReceiveHistoryClick}
										clearReceive={onClickClearReceive}
										validateInvoiceNumber={checkInvoiceNumberIsValidAsync}
									/>
								),
							},
						]}
					/>

					{assignLocation.isModalOpen && (
						<ModalAssignLocation
							isOpen={assignLocation.isModalOpen}
							onSubmit={onLocationSubmit}
							onCancel={() => setAssignLocation({ ...assignLocation, isModalOpen: false })}
							poLocation={pageData.purchaseOrder.shipToLocation.locationId}
							scannedInboundLocation={assignLocation.scannedLocation}
							currentLocation={assignLocation.currentLocation}
							sku={assignLocation.sku}
							context={assignLocation.context as "manual" | "scanned"}
						/>
					)}

					{receive && isModalReceiveFinaliseOpen && (
						<ModalReceiveFinalise
							isOpen={isModalReceiveFinaliseOpen}
							hasExistingCredit={false}
							skipAllocation={ordersAlreadyAllocated}
							onFinaliseSubmit={onSubmitForReview}
							onCancelFinalise={onCloseModalReceiveFinalise}
							receive={receive}
							locationId={pageData.purchaseOrder.shipToLocation.locationId}
							originalReceive={originalReceives.find((r) => r.id === receive.id)}
							hasLabels={enableProductLabelAutoPrint && hasLabels}
							showInvoiceTotals
							showFreightCost={captureFreightCostEnabled}
						/>
					)}

					<ModalHistory
						title="Receive History"
						alertType={alertTypeEnum.INFO}
						primaryButtonName="Ok"
						onPrimaryButtonClick={closingHistoryModal}
						isOpen={receiveHistory.isHistoryVisible}
						onClose={closingHistoryModal}
						children={
							<ReceiveHistory
								statusHistoryList={receiveHistory ? receiveHistory.statusHistoryList ?? [] : []}
								invoiceNumber={receiveHistory ? receiveHistory.invoiceNumber ?? "" : ""}
								sku={receiveHistory.sku}
							/>
						}
						size={modalSize.FULLSCREEN}
					/>
				</>
			)}

			<CommentModal
				isOpen={commentModal.isOpen}
				isDisabled={!receive?.id}
				onClose={onCommentModalClose}
				comments={comments}
				onAddComment={onAddComment(receive?.id ?? "")}
			/>

			{modal.isOpen && <Modal {...modal} />}

			{alertModal.isOpen && (
				<AlertModal
					isOpen={alertModal.isOpen}
					title={alertModal.title}
					status={alertModal.status as "warning" | "error" | "info" | "success"}
					onClose={onCloseAlertModal}
				/>
			)}

			<SpeedDialMenu
				isOpen={isSpeedDialOpen}
				onOpen={onOpenSpeedDial}
				onClose={onCloseSpeedDial}
				actions={[
					{
						title: speedDialAction.finalise,
						onClick: onFinaliseReceivesInReviewClick,
						isDisplay: activeTab === 2 && isInReview,
					},
					{
						title: speedDialAction.submitReview,
						onClick: onSubmitForReviewClick,
						isDisplay:
							activeTab === 1 &&
							receive?.status === receiveStatus.inProgress &&
							(receive?.receiveLineItems ?? []).length > 0,
					},
					{
						title: speedDialAction.saveDraft,
						onClick: onSaveDraftClick,
						isDisplay: activeTab === 1 && receive?.status === receiveStatus.inProgress,
					},
					{
						title: speedDialAction.saveChanges,
						onClick: onSaveReceivesInReviewClick,
						isDisplay: activeTab === 2 && isInReview,
					},
					{
						title: isHardwareView ? speedDialAction.hardwareView : speedDialAction.softwareView,
						onClick: toggleView,
						isDisplay: true,
					},
					{
						title: speedDialAction.clearReceive,
						onClick: onClickClearReceive,
						isDisplay: activeTab === 1 && receive?.status === receiveStatus.inProgress,
					},
					{
						title: speedDialAction.reopenPurchaseOrder,
						onClick: onReopenPoClick,
						isDisplay: canPurchaseOrderBeReopened,
					},
					{
						title: speedDialAction.comments,
						onClick: onCommentModalOpen,
						isDisplay: activeTab === 1 && receive,
					},
				]}
			/>

			<LoadingDialog message={loadingDialog.message} isOpen={loadingDialog.isOpen} />
			<Backdrop open={isBackdropOpen} style={theme.backDrop} />
		</>
	);
};

export default ManualReceiving;
